import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
} from "@mui/material";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import SearchIcon from "@mui/icons-material/Search";
import * as services from "./../../services/saleng";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatNumber } from "../../utils/formatNumber";
import { hasPermission } from "../../auth/RequirePermission3";
import DescriptionIcon from "@mui/icons-material/Description";

const getSaleItemTotal = (item) => {
  const netWeight = Number(item?.netWeight ?? 0) || 0;
  const soUsedWeight = Math.min(
    netWeight,
    Math.max(0, Number(item?.soUsedWeight ?? 0) || 0),
  );
  const soPricePerUnit = Number(item?.soPricePerUnit ?? 0) || 0;

  // ใช้ราคา SO ที่บันทึกในตั๋ว เพื่อให้ยอดบนรายการตั๋วตรงกับหน้ารายละเอียด
  if (soUsedWeight > 0 && soPricePerUnit > 0) {
    const regularPrice = Number(item?.pricePerUnit ?? 0) || 0;
    return (
      soUsedWeight * soPricePerUnit +
      Math.max(0, netWeight - soUsedWeight) * regularPrice
    );
  }

  return Number(item?.netPrice ?? 0) || 0;
};

/* ---------- 16:9 ratio wrapper ---------- */
const RatioImage = ({ src, alt }) => (
  <div className="relative w-full pt-[50%] overflow-hidden bg-gray-100 flex-shrink-0">
    {src ? (
      <img
        src={src}
        alt={alt || "preview"}
        className="absolute inset-0 w-full h-full object-cover block"
      />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center">
        <ImageNotSupportedIcon style={{ fontSize: 40, color: "#9ca3af" }} />
      </div>
    )}
  </div>
);

const CardItem = ({ item, onClick, status }) => {
  const allImages = item.items.flatMap((subItem) => subItem.images || []);

  const latestImageOverall = allImages.length
    ? [...allImages].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0].imageUrl
    : null;

  const getStatusStyles = (s) => {
    switch (s) {
      case "waiting":
        return "bg-blue-50 border-blue-100 ring-1 ring-blue-100";
      case "payed":
        return "bg-green-50 border-green-100 ring-1 ring-green-100";
      case "cancel":
        return "bg-red-50 border-red-100 ring-1 ring-red-100";
      default:
        return "bg-white border-gray-100";
    }
  };

  return (
    <div
      className={`group relative w-full cursor-pointer flex flex-col rounded-2xl shadow-sm border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden h-full ${getStatusStyles(
        status
      )}`}
      onClick={() => onClick(item)}
    >
      <RatioImage src={latestImageOverall} alt="preview" />

      {/* Overlapping Driver Image & Main Info Header */}
      <div className="px-3 relative flex items-end -mt-6 mb-1.5 z-10 pointer-events-none">
        {/* Driver Image */}
        <div className="w-[70px] h-[85px] rounded-lg overflow-hidden shadow-md border-[2px] border-white bg-gray-200 flex-shrink-0 relative">
          {item.driver?.img ? (
            <img
              src={item.driver.img}
              alt={item.driver?.name || "driver"}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-300">
              <ImageNotSupportedIcon style={{ fontSize: 20 }} />
            </div>
          )}
        </div>

        {/* Item Count Badge */}
        <div className="ml-auto mb-0.5">
          <div className="bg-white/90 backdrop-blur-sm border border-gray-100 px-1.5 py-0.5 rounded-md shadow-sm text-[10px] font-bold text-gray-700">
            {item?.items?.length || 0} รายการ
          </div>
        </div>
      </div>

      <div className="px-3 pb-3 mt-2 flex-1 flex flex-col space-y-2">
        {/* Info */}
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[19px] text-black leading-tight">
              <span className="font-normal">ชื่อการขนส่ง: </span>
              <span className="font-bold">
                {item?.shipping?.shippingName || "-"}
              </span>
            </span>
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <h3 className="text-xl font-bold text-gray-900 leading-none">
              #{(item.billNumber || "").split("-").pop()}
            </h3>

          </div>
          <span className="text-[14px]  text-gray-500">
            PO:{" "}
            <span className="font-medium text-gray-700">
              {item.shipping?.purchaseOrderNumber || "-"}
            </span>
          </span>
          <div className="text-base font-bold text-gray-800 mt-1 line-clamp-1 leading-tight">
            {item.customer?.name || "-"}
          </div>
          <div className="text-[16px] font-semibold text-black mt-0 line-clamp-1">
            คนขับ: {item.shipping?.driverName || ""}
          </div>
        </div>

        {/* Car Info */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="bg-white border border-gray-200 rounded px-1.5 py-0.5 flex flex-col items-center min-w-[50px]">
            <span className="text-[11px] text-gray-400 leading-none mb-0.5">
              ทะเบียน
            </span>
            <span className="text-[12px] font-bold text-gray-900 leading-none">
              {item.car?.license || "-"}
            </span>
          </div>
          <div className="text-gray-600 leading-tight">
            <div className="font-medium">
              {item.car?.province || "ไม่ระบุ"}
            </div>
            <div className="text-[15px] font-bold text-black mt-0.5">
              {item.car?.type || ""}
            </div>
          </div>
        </div>

        {/* Price */}
        {hasPermission(["show_price_in_ticket"]) && (
          <div className="bg-green-50 border border-green-100 rounded-md p-1 text-center">
            <span className="text-xs text-green-600 mr-1">ยอดเงิน</span>
            <span className="text-lg font-bold text-green-700">
              {formatNumber(
                (item.items || []).reduce(
                  (sum, i) => sum + getSaleItemTotal(i),
                  0
                )
              )}
            </span>
            <span className="text-xs text-green-600 ml-1">บาท</span>
          </div>
        )}

        {/* Note */}
        {Boolean(item?.note) && (
          <div className="bg-gray-100/50 rounded p-1.5 flex gap-1 items-start">
            <DescriptionIcon
              style={{ fontSize: 16, marginTop: 1 }}
              className="text-gray-400"
            />
            <p className="text-xs text-gray-700 line-clamp-1 leading-normal">
              {item.note}
            </p>
          </div>
        )}

        <div className="flex-grow" />

        {/* Footer Dates */}
        <div className="pt-1 mt-0.5 border-t border-gray-200/50 text-xs text-gray-400 space-y-0.5">
          <div className="flex justify-between text-[15px]">
            <span className="font-semibold text-gray-600">วันที่เปิด:</span>
            <span className="font-semibold text-gray-700">
              {new Date(item.createdAt).toLocaleDateString("th-TH", {
                day: "numeric",
                month: "short",
              })}{" "}
              {new Date(item.createdAt).toLocaleTimeString("th-TH", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          {status === "payed" && item.paidAt && (
            <div className="flex justify-between text-green-600">
              <span>จ่าย:</span>
              <span className="font-medium">
                {new Date(item.paidAt).toLocaleDateString("th-TH", {
                  day: "numeric",
                  month: "short",
                })}{" "}
                {new Date(item.paidAt).toLocaleTimeString("th-TH", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ImageCardGrid() {
  const [isLoading, setIsLoading] = useState(true);
  const [transaction, setTransaction] = useState([]);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");

  const formatDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    if (status !== "payed") date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  // ---- ดึงข้อมูล ----
  useEffect(() => {
    let interval;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await services.getSaleTransactionByStatus(
          status,
          startDate,
          endDate
        );
        const data = res.data?.data ?? [];
        setTransaction(data);
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchData();
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [startDate, endDate, status]);

  // ----------- ดึงข้อมูลสินค้า -----------
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await services.getProductsBranch(true);
        setProducts(res.data?.data || []);
      } catch (err) {
        console.error("fetch products error", err);
      }
    };
    fetchProducts();
  }, []);

  const handleTicketSelect = (data) => {
    navigate(
      `/sale/checkout?id=${data.transactionId}&ticket=${data.billNumber}&status=${data.status}`
    );
  };

  const filtered = (transaction || []).filter((item) => {
    const query = search.trim().toLowerCase();

    const id = item.id?.toString() || "";
    const customerName = item.customer?.name || item.customerName || "";
    const bill = item.billNumber || "";
    const carNumber = item.carNumber || item.car?.license || "";

    const carTokens =
      carNumber?.match(/[ก-ฮ]+|[0-9]+/g)?.map((s) => s.toLowerCase()) || [];

    const normalizedQuery = query.replace(/^#/, "");

    // --- Product Filter ---
    const matchesProduct = !selectedProduct ||
      (item.items || []).some(sub => String(sub.productId || sub.product?.id) === String(selectedProduct.id));

    if (!matchesProduct) return false;

    return (
      id.includes(normalizedQuery) ||
      customerName.toLowerCase().includes(normalizedQuery) ||
      bill.toLowerCase().includes(normalizedQuery) ||
      carNumber.toLowerCase().includes(normalizedQuery) ||
      carTokens.some((token) => token.includes(normalizedQuery)) ||
      (item.items || []).some(it => (it.product?.title || it.productName || "").toLowerCase().includes(normalizedQuery))
    );
  });

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 md:p-8">
      <AppBreadcrumbs />

      {/* ฟิลเตอร์ */}
      <div className="flex flex-col xl:flex-row gap-4 my-6 items-end justify-between bg-white p-5 rounded-2xl shadow-sm border border-gray-100">

        {/* Left: Search & Product */}
        <div className="flex flex-col md:flex-row gap-4 w-full xl:w-auto flex-1">
          <div className="flex-1 md:max-w-md">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">ค้นหาด่วน</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="เลขที่บิล, ชื่อลูกค้า, ทะเบียนรถ..."
                className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 block w-full pl-10 p-2.5 outline-none transition-all"
                value={search}
                onChange={(e) => setSearch(e.target.value.trimStart())}
              />
            </div>
          </div>

          <div className="w-full md:w-72">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">กรองสินค้า</label>
            <Autocomplete
              size="small"
              options={products}
              getOptionLabel={(option) => option.title || ""}
              value={selectedProduct}
              onChange={(_, newValue) => setSelectedProduct(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="สินค้าทั้งหมด..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f9fafb', // gray-50
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      '& fieldset': {
                        borderColor: '#e5e7eb', // gray-200
                      },
                      '&:hover fieldset': {
                        borderColor: '#10b981', // green-500
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#10b981',
                        borderWidth: '1px',
                      }
                    }
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* Right: Dates */}
        <div className="flex flex-row gap-3 w-full xl:w-auto">
          <div className="flex-1 md:w-40">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
              ตั้งแต่วันที่
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 block w-full p-2 outline-none transition-all h-[40px]"
            />
          </div>
          <div className="flex-1 md:w-40">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
              ถึงวันที่
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 block w-full p-2 outline-none transition-all h-[40px]"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6 ${isLoading ? "opacity-60 pointer-events-none" : ""
          }`}
      >
        {filtered.map((item) => (
          <CardItem
            key={item.id}
            item={item}
            onClick={handleTicketSelect}
            status={status}
          />
        ))}
      </div>

      {!isLoading && filtered.length === 0 && (
        <div className="text-center text-gray-500 py-12 bg-white rounded-xl border border-gray-200 shadow-sm mt-4">
          <p>ไม่พบรายการตามเงื่อนไขที่ค้นหา</p>
        </div>
      )}
    </div>
  );
}
